import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { toggleReaction } from './api';
import { useDialog } from '../ui/Dialogs';
import { REACTION_EMOJIS } from './reactions';
import type { Reaction } from './types';

// Agrupa las reacciones por emoji respetando el orden del set fijo.
function group(reactions: Reaction[], currentUserId: string | undefined) {
  return REACTION_EMOJIS.map((emoji) => {
    const forEmoji = reactions.filter((r) => r.emoji === emoji);
    return {
      emoji,
      count: forEmoji.length,
      mine: currentUserId != null && forEmoji.some((r) => r.user_id === currentUserId),
      userIds: forEmoji.map((r) => r.user_id),
    };
  });
}

// Texto "Alejandro, María y tú" para el tooltip de un chip. La reacción propia
// se muestra como "tú" y se deja al final; los user_id sin nombre conocido
// (miembro que ya no está en el equipo activo) caen a "Alguien".
function reactorsLabel(
  userIds: string[],
  currentUserId: string | undefined,
  nameById: Map<string, string> | undefined,
): string {
  const others = userIds
    .filter((id) => id !== currentUserId)
    .map((id) => nameById?.get(id) ?? 'Alguien');
  const names = userIds.includes(currentUserId ?? '') ? [...others, 'tú'] : others;
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
}

async function applyToggle(
  messageId: string,
  emoji: string,
  mine: boolean,
  setPending: (v: string | null) => void,
  onError: (msg: string) => void,
) {
  setPending(emoji);
  try {
    await toggleReaction(messageId, emoji, mine);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    onError(msg);
  } finally {
    setPending(null);
  }
}

interface Props {
  messageId: string;
  reactions: Reaction[];
  currentUserId: string | undefined;
  nameById?: Map<string, string>;
}

// Chips de las reacciones que YA tienen votos. Si no hay ninguna, no pinta nada
// (clave para no ensuciar hilos largos). Clic en un chip = alterna tu voto.
export function ReactionChips({ messageId, reactions, currentUserId, nameById }: Props) {
  const [pending, setPending] = useState<string | null>(null);
  const { alert } = useDialog();
  const notifyError = (msg: string) => alert({ title: 'No se pudo reaccionar', message: msg });
  const groups = group(reactions, currentUserId).filter((g) => g.count > 0);
  if (groups.length === 0) return null;

  return (
    <div style={styles.chips}>
      {groups.map(({ emoji, count, mine, userIds }) => (
        <button
          key={emoji}
          type="button"
          disabled={pending != null}
          onClick={() => applyToggle(messageId, emoji, mine, setPending, notifyError)}
          title={reactorsLabel(userIds, currentUserId, nameById)}
          style={{ ...styles.chip, ...(mine ? styles.chipMine : null) }}
        >
          <span style={styles.emoji}>{emoji}</span>
          <span style={styles.count}>{count}</span>
        </button>
      ))}
    </div>
  );
}

// Botón "añadir reacción" (carita + +) que solo aparece en hover del mensaje
// (regla CSS .msg-row:hover .reaction-add). Al pulsar despliega el set fijo.
export function ReactionAddButton({ messageId, reactions, currentUserId }: Props) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const { alert } = useDialog();
  const notifyError = (msg: string) => alert({ title: 'No se pudo reaccionar', message: msg });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = async (emoji: string, mine: boolean) => {
    await applyToggle(messageId, emoji, mine, setPending, notifyError);
    setOpen(false);
  };

  const groups = group(reactions, currentUserId);

  return (
    <span ref={wrapRef} style={styles.wrap}>
      <button
        type="button"
        className={`reaction-add${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title="Añadir reacción"
        style={styles.trigger}
      >
        <span style={styles.triggerFace}>🙂</span>
        <span style={styles.triggerPlus}>+</span>
      </button>
      {open && (
        <div className="reaction-popover">
          {groups.map(({ emoji, mine }) => (
            <button
              key={emoji}
              type="button"
              disabled={pending != null}
              onClick={() => pick(emoji, mine)}
              title={mine ? 'Quitar tu reacción' : 'Reaccionar'}
              style={{ ...styles.popItem, ...(mine ? styles.popItemMine : null) }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

const styles: Record<string, CSSProperties> = {
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 24,
    padding: '0 8px',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    background: '#f1f5f9',
    cursor: 'pointer',
    fontSize: 13,
    lineHeight: 1,
  },
  chipMine: {
    background: '#dbeafe',
    borderColor: '#bfdbfe',
  },
  emoji: {
    fontSize: 14,
  },
  count: {
    fontSize: 12,
    fontWeight: 700,
    color: '#475569',
  },
  wrap: {
    position: 'relative',
    display: 'inline-flex',
    marginLeft: 4,
  },
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    height: 24,
    padding: '0 6px',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    background: '#fff',
    cursor: 'pointer',
    lineHeight: 1,
  },
  triggerFace: {
    fontSize: 13,
    filter: 'grayscale(0.2)',
  },
  triggerPlus: {
    fontSize: 12,
    fontWeight: 700,
    color: '#94a3b8',
  },
  popItem: {
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid transparent',
    borderRadius: 8,
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
  },
  popItemMine: {
    background: '#dbeafe',
    borderColor: '#bfdbfe',
  },
};
